import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import companyApi from "../../api/companyApi";
import { CURRENCY_OPTIONS, formatProductionUnit } from "./productionUtils";
import { formatLastUpdated, getStatusChipProps } from "./statusUtils";
import { formatNumberWithCommas } from "../../utils/companiesFormatting";

const FORECAST_HORIZON_YEARS = 10;

const createCommodityRow = () => ({
  commodity_id: "",
  values: { ounces_produced: {}, aisc: {} },
});

const buildForecastYearColumns = (startYear) => {
  const resolvedStart = Number(startYear) || new Date().getFullYear() + 1;
  const years = Array.from({ length: FORECAST_HORIZON_YEARS }, (_, idx) => resolvedStart + idx);
  const catchAllYear = resolvedStart + FORECAST_HORIZON_YEARS;
  return [
    ...years.map((year) => ({ key: String(year), label: String(year), year })),
    { key: "10_plus", label: "10+ yrs", year: catchAllYear },
  ];
};

const getRowValueStats = (row, yearColumns) => {
  let hasProduced = false;
  let hasAisc = false;
  yearColumns.forEach((col) => {
    const produced = row.values?.ounces_produced?.[col.year];
    const aisc = row.values?.aisc?.[col.year];
    if (produced !== "" && produced !== null && produced !== undefined) {
      hasProduced = true;
    }
    if (aisc !== "" && aisc !== null && aisc !== undefined) {
      hasAisc = true;
    }
  });
  return { hasProduced, hasAisc, hasValues: hasProduced || hasAisc };
};

const buildDisplayYears = (values, startYear) => {
  const years = Array.from(
    new Set((values || []).map((val) => val.forecast_year).filter(Boolean))
  ).sort((a, b) => a - b);
  if (!years.length && startYear) {
    const resolvedStart = Number(startYear) || new Date().getFullYear() + 1;
    const horizon = Array.from({ length: FORECAST_HORIZON_YEARS }, (_, idx) => resolvedStart + idx);
    const catchAll = resolvedStart + FORECAST_HORIZON_YEARS;
    return [
      ...horizon.map((year) => ({ year, label: String(year) })),
      { year: catchAll, label: "10+ yrs" },
    ];
  }
  const minYear = years[0];
  return years.map((year, index) => ({
    year,
    label:
      index === years.length - 1 && minYear && year >= minYear + FORECAST_HORIZON_YEARS
        ? "10+ yrs"
        : String(year),
  }));
};

export default function ForecastSection({
  assets,
  resourceReservesByAsset = {},
  companyId,
  canEdit,
  commodities,
  productionMetrics,
  defaultReportingCurrency,
  forecasts = [],
  loading = false,
  onForecastSaved,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [forecastDialogMode, setForecastDialogMode] = useState("create");
  const [editingForecastId, setEditingForecastId] = useState(null);
  const [forecastNotice, setForecastNotice] = useState(null);
  const [deleteForecastTarget, setDeleteForecastTarget] = useState(null);
  const [draftForecastMeta, setDraftForecastMeta] = useState({
    name: "",
    as_of_date: "",
    start_year: "",
    reporting_currency: defaultReportingCurrency || "USD",
    source_title: "",
    source_url: "",
    source_notes: "",
  });
  const [selectedAssetIds, setSelectedAssetIds] = useState([]);
  const [focusedAssetId, setFocusedAssetId] = useState(null);
  const [draftByAsset, setDraftByAsset] = useState({});
  const [saveState, setSaveState] = useState({ loading: false, error: null, success: null });
  const [startYearTouched, setStartYearTouched] = useState(false);

  const assetsAvailable = useMemo(() => {
    const reserves = [];
    (assets || []).forEach((asset) => {
      const assetReserves = resourceReservesByAsset[asset.id] || [];
      assetReserves.forEach((reserve) => {
        if (!reserve?.id) return;
        const reserveName = reserve.name || `Reserve ${reserve.id}`;
        reserves.push({
          ...reserve,
          asset_id: asset.id,
          asset_name: `${asset.asset_name} — ${reserveName}`,
        });
      });
    });
    return reserves;
  }, [assets, resourceReservesByAsset]);
  const assetsById = useMemo(
    () => new Map(assetsAvailable.map((asset) => [asset.id, asset])),
    [assetsAvailable]
  );
  const hasAssets = assetsAvailable.length > 0;

  const metricUnits = useMemo(() => {
    const ouncesMetric = productionMetrics.find((metric) => metric.code === "ounces_produced");
    const aiscMetric = productionMetrics.find((metric) => metric.code === "aisc");
    return {
      ounces_produced: ouncesMetric?.unit,
      aisc: aiscMetric?.unit,
    };
  }, [productionMetrics]);

  const metricIds = useMemo(() => {
    const ouncesMetric = productionMetrics.find((metric) => metric.code === "ounces_produced");
    const aiscMetric = productionMetrics.find((metric) => metric.code === "aisc");
    return {
      ounces_produced: ouncesMetric?.id,
      aisc: aiscMetric?.id,
    };
  }, [productionMetrics]);

  const resolvedStartYear = useMemo(() => {
    const numeric = Number(draftForecastMeta.start_year);
    if (!Number.isNaN(numeric) && numeric > 0) return numeric;
    if (draftForecastMeta.as_of_date) {
      const parsed = new Date(draftForecastMeta.as_of_date);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed.getFullYear() + 1;
      }
    }
    return new Date().getFullYear() + 1;
  }, [draftForecastMeta.as_of_date, draftForecastMeta.start_year]);

  const forecastYearColumns = useMemo(
    () => buildForecastYearColumns(resolvedStartYear),
    [resolvedStartYear]
  );

  useEffect(() => {
    if (!defaultReportingCurrency) return;
    setDraftForecastMeta((prev) => ({
      ...prev,
      reporting_currency: prev.reporting_currency || defaultReportingCurrency,
    }));
  }, [defaultReportingCurrency]);

  useEffect(() => {
    if (!focusedAssetId) return;
    setDraftByAsset((prev) => {
      if (prev[focusedAssetId]) return prev;
      return {
        ...prev,
        [focusedAssetId]: { commodityRows: [createCommodityRow()] },
      };
    });
  }, [focusedAssetId]);

  const handleOpenDialog = () => {
    setDraftForecastMeta({
      name: "",
      as_of_date: "",
      start_year: "",
      reporting_currency: defaultReportingCurrency || "USD",
      source_title: "",
      source_url: "",
      source_notes: "",
    });
    setSelectedAssetIds([]);
    setFocusedAssetId(null);
    setDraftByAsset({});
    setSaveState({ loading: false, error: null, success: null });
    setStartYearTouched(false);
    setForecastDialogMode("create");
    setEditingForecastId(null);
    setForecastNotice(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSaveState({ loading: false, error: null, success: null });
    setForecastNotice(null);
  };

  const buildDraftFromForecast = (forecast) => {
    const reports = forecast?.resource_reports || [];
    const selectedIds = reports
      .map((report) => report.resource_reserve?.id || report.resource_reserve)
      .filter(Boolean);
    const draft = {};
    reports.forEach((report) => {
      const reserveId = report.resource_reserve?.id || report.resource_reserve;
      if (!reserveId) return;
      const rowsByCommodity = {};
      (report.values || []).forEach((val) => {
        const commodityId = val.commodity?.id || val.commodity;
        const metricCode = val.metric?.code;
        if (!commodityId || !metricCode) return;
        if (!rowsByCommodity[commodityId]) {
          rowsByCommodity[commodityId] = createCommodityRow();
          rowsByCommodity[commodityId].commodity_id = commodityId;
        }
        rowsByCommodity[commodityId].values[metricCode] = {
          ...(rowsByCommodity[commodityId].values[metricCode] || {}),
          [val.forecast_year]: val.value ?? "",
        };
      });
      const rows = Object.values(rowsByCommodity);
      draft[reserveId] = {
        commodityRows: rows.length ? rows : [createCommodityRow()],
      };
    });
    return {
      selectedIds,
      draftByAsset: draft,
    };
  };

  const handleEditForecast = (forecast) => {
    const { selectedIds, draftByAsset: draft } = buildDraftFromForecast(forecast);
    setDraftForecastMeta({
      name: forecast.name || "",
      as_of_date: forecast.as_of_date || "",
      start_year: forecast.start_year || "",
      reporting_currency: forecast.reporting_currency || defaultReportingCurrency || "USD",
      source_title: forecast.source_title || "",
      source_url: forecast.source_url || "",
      source_notes: forecast.source_notes || "",
    });
    setSelectedAssetIds(selectedIds);
    setFocusedAssetId(selectedIds[0] || null);
    setDraftByAsset(draft);
    setSaveState({ loading: false, error: null, success: null });
    setForecastDialogMode("edit");
    setEditingForecastId(forecast.id);
    setForecastNotice(null);
    setStartYearTouched(true);
    setDialogOpen(true);
  };

  const handleDuplicateForecast = (forecast) => {
    const { selectedIds, draftByAsset: draft } = buildDraftFromForecast(forecast);
    setDraftForecastMeta({
      name: forecast.name ? `${forecast.name} (Copy)` : "Forecast copy",
      as_of_date: forecast.as_of_date || "",
      start_year: forecast.start_year || "",
      reporting_currency: forecast.reporting_currency || defaultReportingCurrency || "USD",
      source_title: forecast.source_title || "",
      source_url: forecast.source_url || "",
      source_notes: forecast.source_notes || "",
    });
    setSelectedAssetIds(selectedIds);
    setFocusedAssetId(selectedIds[0] || null);
    setDraftByAsset(draft);
    setSaveState({ loading: false, error: null, success: null });
    setForecastDialogMode("duplicate");
    setEditingForecastId(null);
    setForecastNotice("Forecast copied. Save to create a new draft.");
    setStartYearTouched(true);
    setDialogOpen(true);
  };

  const handleRequestDeleteForecast = (forecast) => {
    setDeleteForecastTarget(forecast);
  };

  const handleConfirmDeleteForecast = async () => {
    if (!deleteForecastTarget) return;
    const reports = deleteForecastTarget.resource_reports || [];
    try {
      const deletes = reports
        .map((report) => report?.id)
        .filter(Boolean)
        .map((reportId) => companyApi.deleteProductionReport(reportId));
      if (deletes.length) {
        await Promise.all(deletes);
      }
    } catch {
      // best effort; allow UI refresh via callback
    } finally {
      setDeleteForecastTarget(null);
      if (onForecastSaved) {
        onForecastSaved();
      }
    }
  };

  const handleMetaChange = (field) => (event) => {
    const value = event.target.value;
    setDraftForecastMeta((prev) => ({ ...prev, [field]: value }));
    if (field === "start_year") {
      setStartYearTouched(true);
      return;
    }
    if (field === "as_of_date" && !startYearTouched) {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) {
        setDraftForecastMeta((prev) => ({
          ...prev,
          start_year: parsed.getFullYear() + 1,
          as_of_date: value,
        }));
      }
    }
  };

  const handleAssetsChange = (_event, values) => {
    const ids = values.map((asset) => asset.id);
    setSelectedAssetIds(ids);
    if (!ids.length) {
      setFocusedAssetId(null);
      return;
    }
    if (!focusedAssetId || !ids.includes(focusedAssetId)) {
      setFocusedAssetId(ids[0]);
    }
  };

  const handleFocusAsset = (assetId) => {
    setFocusedAssetId(assetId);
  };

  const handleAddCommodityRow = () => {
    if (!focusedAssetId) return;
    setDraftByAsset((prev) => ({
      ...prev,
      [focusedAssetId]: {
        commodityRows: [
          ...(prev[focusedAssetId]?.commodityRows || []),
          createCommodityRow(),
        ],
      },
    }));
  };

  const handleRemoveCommodityRow = (rowIndex) => {
    if (!focusedAssetId) return;
    setDraftByAsset((prev) => ({
      ...prev,
      [focusedAssetId]: {
        commodityRows: (prev[focusedAssetId]?.commodityRows || []).filter(
          (_, idx) => idx !== rowIndex
        ),
      },
    }));
  };

  const handleCommodityChange = (rowIndex, commodityId) => {
    if (!focusedAssetId) return;
    setDraftByAsset((prev) => ({
      ...prev,
      [focusedAssetId]: {
        commodityRows: (prev[focusedAssetId]?.commodityRows || []).map((row, idx) =>
          idx === rowIndex ? { ...row, commodity_id: commodityId } : row
        ),
      },
    }));
  };

  const handleValueChange = (rowIndex, metricKey, year, value) => {
    if (!focusedAssetId) return;
    setDraftByAsset((prev) => ({
      ...prev,
      [focusedAssetId]: {
        commodityRows: (prev[focusedAssetId]?.commodityRows || []).map((row, idx) =>
          idx === rowIndex
            ? {
                ...row,
                values: {
                  ...row.values,
                  [metricKey]: {
                    ...(row.values?.[metricKey] || {}),
                    [year]: value,
                  },
                },
              }
            : row
        ),
      },
    }));
  };

  const getAssetStatus = (assetId) => {
    const rows = draftByAsset[assetId]?.commodityRows || [];
    if (!rows.length) return { label: "No data", color: "default" };
    let hasAnyValues = false;
    let allComplete = true;
    rows.forEach((row) => {
      const stats = getRowValueStats(row, forecastYearColumns);
      if (!stats.hasValues) {
        return;
      }
      hasAnyValues = true;
      if (!row.commodity_id || !stats.hasProduced || !stats.hasAisc) {
        allComplete = false;
      }
    });
    if (!hasAnyValues) return { label: "No data", color: "default" };
    if (allComplete) return { label: "Complete", color: "success" };
    return { label: "Partial", color: "warning" };
  };

  const handleNavigateAsset = (direction) => {
    if (!focusedAssetId) return;
    const index = selectedAssetIds.indexOf(focusedAssetId);
    if (index === -1) return;
    const nextIndex = direction === "next" ? index + 1 : index - 1;
    if (nextIndex < 0 || nextIndex >= selectedAssetIds.length) return;
    setFocusedAssetId(selectedAssetIds[nextIndex]);
  };

  const handleSaveForecast = async () => {
    if (!companyId) {
      setSaveState({ loading: false, error: "Company is required.", success: null });
      return;
    }
    if (!draftForecastMeta.name?.trim()) {
      setSaveState({ loading: false, error: "Forecast name is required.", success: null });
      return;
    }
    if (!draftForecastMeta.as_of_date) {
      setSaveState({ loading: false, error: "As-of date is required.", success: null });
      return;
    }
    if (!selectedAssetIds.length) {
      setSaveState({
        loading: false,
        error: "Select at least one resource reserve to forecast.",
        success: null,
      });
      return;
    }
    if (!metricIds.ounces_produced || !metricIds.aisc) {
      setSaveState({
        loading: false,
        error: "Forecast metrics (ounces produced, AISC) are not available.",
        success: null,
      });
      return;
    }

    const hasAnyValues = selectedAssetIds.some((assetId) => {
      const rows = draftByAsset[assetId]?.commodityRows || [];
      return rows.some((row) => getRowValueStats(row, forecastYearColumns).hasValues);
    });
    if (!hasAnyValues) {
      setSaveState({
        loading: false,
        error: "Add at least one forecast value before saving.",
        success: null,
      });
      return;
    }

    const invalidRow = selectedAssetIds.some((assetId) => {
      const rows = draftByAsset[assetId]?.commodityRows || [];
      return rows.some((row) => {
        const stats = getRowValueStats(row, forecastYearColumns);
        return stats.hasValues && !row.commodity_id;
      });
    });
    if (invalidRow) {
      setSaveState({
        loading: false,
        error: "Select a commodity for each forecast row with values.",
        success: null,
      });
      return;
    }

    setSaveState({ loading: true, error: null, success: null });
    try {
      const forecastMetaPayload = {
        name: draftForecastMeta.name.trim(),
        as_of_date: draftForecastMeta.as_of_date,
        start_year: resolvedStartYear,
        reporting_currency: draftForecastMeta.reporting_currency || "USD",
        source_title: draftForecastMeta.source_title || "",
        source_url: draftForecastMeta.source_url || "",
        source_notes: draftForecastMeta.source_notes || "",
      };

      let createdForecastId = editingForecastId;
      let includeForecastMeta = true;
      const reportByAssetId =
        forecastDialogMode === "edit"
          ? new Map(
              (
                (forecasts || []).find((item) => item.id === editingForecastId)
                  ?.resource_reports || []
              )
                .map((report) => ({
                  assetId: report.resource_reserve?.id || report.resource_reserve,
                  reportId: report.id,
                }))
                .filter((entry) => entry.assetId && entry.reportId)
                .map((entry) => [entry.assetId, entry.reportId])
            )
          : new Map();
      for (const assetId of selectedAssetIds) {
        const rows = draftByAsset[assetId]?.commodityRows || [];
        const valuesByKey = new Map();
        rows.forEach((row) => {
          if (!row.commodity_id) return;
          forecastYearColumns.forEach((col) => {
            const produced = row.values?.ounces_produced?.[col.year];
            if (produced !== "" && produced !== null && produced !== undefined) {
              const key = `${metricIds.ounces_produced}-${row.commodity_id}-${col.year}`;
              valuesByKey.set(key, {
                metric: metricIds.ounces_produced,
                commodity: row.commodity_id,
                forecast_year: col.year,
                value: Number(produced),
              });
            }
            const aisc = row.values?.aisc?.[col.year];
            if (aisc !== "" && aisc !== null && aisc !== undefined) {
              const key = `${metricIds.aisc}-${row.commodity_id}-${col.year}`;
              valuesByKey.set(key, {
                metric: metricIds.aisc,
                commodity: row.commodity_id,
                forecast_year: col.year,
                value: Number(aisc),
              });
            }
          });
        });
        const values = Array.from(valuesByKey.values());
        if (!values.length) continue;

        const reportPayload = {
          resource_reserve: assetId,
          kind: "forecast",
          as_of_date: draftForecastMeta.as_of_date,
          forecast: createdForecastId || undefined,
          forecast_meta: includeForecastMeta ? forecastMetaPayload : undefined,
        };

        const reportId = reportByAssetId.get(assetId) || null;
        const report = await companyApi.saveProductionReportWithValues(
          reportPayload,
          values,
          reportId
        );
        if (!createdForecastId && report?.forecast) {
          createdForecastId = report.forecast;
        }
        includeForecastMeta = false;
      }

      setSaveState({ loading: false, error: null, success: "Forecast saved." });
      setDialogOpen(false);
      if (onForecastSaved) {
        onForecastSaved();
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        "Unable to save forecast.";
      setSaveState({ loading: false, error: detail, success: null });
    }
  };

  const focusedAsset = focusedAssetId ? assetsById.get(focusedAssetId) : null;
  const focusedDraft = focusedAssetId ? draftByAsset[focusedAssetId] : null;
  const focusedRows = focusedDraft?.commodityRows || [];

  return (
    <Box
      sx={(theme) => ({
        ...theme.mixins.frostedBox,
        p: { xs: 0.5, md: 3 },
        height: "100%",
      })}
    >
      <Stack spacing={{ xs: 2, sm: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Forecasts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create forward-looking production forecasts for the company and its resource reserves.
          </Typography>
        </Box>

        {!hasAssets ? (
          <Box
            sx={{
              border: "1px dashed rgba(0,0,0,0.2)",
              borderRadius: 2,
              p: 3,
              textAlign: "center",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Add resource reserves to create a forecast
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Create at least one resource reserve before building a forecast scenario.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={{ xs: 2, sm: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: "space-between",
                gap: { xs: 1, sm: 1 },
              }}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                Existing forecasts
              </Typography>
              <Button
                variant="contained"
                onClick={handleOpenDialog}
                disabled={!canEdit}
                fullWidth={isMobile}
                size={isMobile ? "small" : "medium"}
              >
                Add Forecast
              </Button>
            </Box>
            {loading ? (
              <Typography variant="body2" color="text.secondary">
                Loading forecasts...
              </Typography>
            ) : forecasts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No forecasts created yet.
              </Typography>
            ) : (
              <Stack spacing={{ xs: 2, sm: 1.5 }}>
                {forecasts.map((forecast) => (
                  <Accordion
                    key={forecast.id}
                    sx={{
                      border: "1px solid rgba(0,0,0,0.08)",
                      borderRadius: 1.5,
                      backgroundColor: "#fff",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ px: { xs: 0.5, sm: 1.5 }, py: { xs: 0.75, sm: 0.5 } }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={{ xs: 1, sm: 1 }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        sx={{ width: "100%" }}
                      >
                        <Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="subtitle1" fontWeight={700}>
                            {forecast.name}
                          </Typography>
                          {forecast.status && (
                            <Chip
                              size="small"
                              variant="outlined"
                              {...getStatusChipProps(forecast.status)}
                            />
                          )}
                          <Typography variant="caption" color="text.secondary">
                            Last updated: {formatLastUpdated(forecast.last_updated_at)}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          As-of date: {forecast.as_of_date || "Not set"}{" "}
                          {forecast.start_year ? `| Start year: ${forecast.start_year}` : ""}
                        </Typography>
                      </Box>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mt: { xs: 1, sm: 0 } }}
                      >
                          <Button
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleEditForecast(forecast);
                            }}
                            disabled={!canEdit}
                            sx={{ minWidth: "auto", px: 1 }}
                          >
                            <EditIcon fontSize="small" />
                          </Button>
                          <Button
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleDuplicateForecast(forecast);
                            }}
                            disabled={!canEdit}
                            sx={{ minWidth: "auto", px: 0 }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleRequestDeleteForecast(forecast);
                            }}
                            disabled={!canEdit}
                            sx={{ minWidth: "auto", px: 1.25 }}
                          >
                            <CloseIcon fontSize="small" />
                          </Button>
                        </Stack>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: { xs: 0.5, sm: 1.5 } }}>
                      {(forecast.resource_reports || []).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No resource-level forecasts saved yet.
                        </Typography>
                      ) : (
                        <Stack spacing={{ xs: 2, sm: 2 }}>
                          {(forecast.resource_reports || []).map((report) => {
                            const reserveId =
                              report.resource_reserve?.id || report.resource_reserve;
                            const reserve = assetsById.get(reserveId);
                            const values = report.values || [];
                            const displayYears = buildDisplayYears(
                              values,
                              forecast.start_year
                            );
                            const grouped = values.reduce((acc, val) => {
                              const commodityName = val.commodity?.name || "Commodity";
                              const metricName = val.metric?.name || val.metric?.code || "Metric";
                              const key = `${commodityName}::${metricName}`;
                              if (!acc[key]) {
                                acc[key] = {
                                  commodityName,
                                  metricName,
                                  metricUnit: val.metric?.unit,
                                  values: {},
                                };
                              }
                              if (val.forecast_year) {
                                acc[key].values[val.forecast_year] = val.value;
                              }
                              return acc;
                            }, {});
                            const rows = Object.values(grouped);
                            return (
                              <Box key={report.id || reserveId}>
                                <Typography variant="subtitle2" fontWeight={700}>
                                  {reserve?.asset_name || "Resource reserve"}
                                </Typography>
                                {rows.length === 0 ? (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 0.5 }}
                                  >
                                    No forecast values yet.
                                  </Typography>
                                ) : (
                                  <Box
                                    sx={{
                                      border: "1px solid rgba(0,0,0,0.08)",
                                      borderRadius: 1.25,
                                      overflowX: { xs: "scroll", md: "hidden" },
                                      WebkitOverflowScrolling: "touch",
                                      mt: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "grid",
                                        gridTemplateColumns: `1.2fr 1.4fr repeat(${displayYears.length}, 1fr)`,
                                        gap: { xs: 0.5, sm: 1 },
                                        px: { xs: 0.5, sm: 1 },
                                        py: { xs: 0.4, sm: 0.5 },
                                        backgroundColor: "rgba(0,0,0,0.03)",
                                      }}
                                    >
                                      <Typography variant="caption" fontWeight={700}>
                                        Commodity
                                      </Typography>
                                      <Typography variant="caption" fontWeight={700}>
                                        Metric
                                      </Typography>
                                      {displayYears.map((col) => (
                                        <Typography
                                          key={`forecast-year-${col.year}`}
                                          variant="caption"
                                          fontWeight={700}
                                        >
                                          {col.label}
                                        </Typography>
                                      ))}
                                    </Box>
                                    <Stack spacing={0} sx={{ px: { xs: 0.5, sm: 1 }, py: { xs: 0.5, sm: 0.75 } }}>
                                      {rows.map((row) => (
                                        <Box
                                          key={`${row.commodityName}-${row.metricName}`}
                                          sx={{
                                            display: "grid",
                                            gridTemplateColumns: `1.2fr 1.4fr repeat(${displayYears.length}, 1fr)`,
                                            gap: { xs: 0.5, sm: 1 },
                                            alignItems: "center",
                                            py: 0.5,
                                          }}
                                        >
                                          <Typography variant="caption" fontWeight={600}>
                                            {row.commodityName}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {row.metricName} (
                                            {formatProductionUnit(
                                              row.metricUnit,
                                              report.reporting_currency ||
                                                forecast.reporting_currency
                                            )}
                                            )
                                          </Typography>
                                          {displayYears.map((col) => (
                                            <Typography
                                              key={`${row.metricName}-${col.year}`}
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              {formatNumberWithCommas(row.values[col.year])}
                                            </Typography>
                                          ))}
                                        </Box>
                                      ))}
                                    </Stack>
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </Stack>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullScreen={isMobile}
        fullWidth
        maxWidth="xl"
        scroll="paper"
        PaperProps={{
          sx: {
            height: { xs: "100%", md: "92vh" },
            maxHeight: { xs: "100%", md: "92vh" },
          },
        }}
      >
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6" fontWeight={700}>
            Forecast Workspace
          </Typography>
          <Button onClick={handleCloseDialog} sx={{ minWidth: "auto" }}>
            <CloseIcon fontSize="small" />
          </Button>
        </DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 2, md: 3 } }}>
          <Stack spacing={3}>
            {forecastNotice && <Alert severity="info">{forecastNotice}</Alert>}
            {saveState.error && <Alert severity="error">{saveState.error}</Alert>}
            {saveState.success && <Alert severity="success">{saveState.success}</Alert>}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Forecast meta
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
                  gap: 2,
                }}
              >
                <TextField
                  label="Forecast name"
                  value={draftForecastMeta.name}
                  onChange={handleMetaChange("name")}
                  fullWidth
                  required
                />
                <TextField
                  label="As-of date"
                  type="date"
                  value={draftForecastMeta.as_of_date}
                  onChange={handleMetaChange("as_of_date")}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  required
                />
                <TextField
                  label="Start year"
                  type="number"
                  value={draftForecastMeta.start_year || resolvedStartYear}
                  onChange={handleMetaChange("start_year")}
                  fullWidth
                  required
                />
                <TextField
                  select
                  label="Reporting currency"
                  value={draftForecastMeta.reporting_currency || "USD"}
                  onChange={handleMetaChange("reporting_currency")}
                  fullWidth
                >
                  {CURRENCY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2,
                  mt: 2,
                }}
              >
                <TextField
                  label="Source title"
                  value={draftForecastMeta.source_title}
                  onChange={handleMetaChange("source_title")}
                  fullWidth
                />
                <TextField
                  label="Source URL"
                  value={draftForecastMeta.source_url}
                  onChange={handleMetaChange("source_url")}
                  fullWidth
                />
                <TextField
                  label="Source notes"
                  value={draftForecastMeta.source_notes}
                  onChange={handleMetaChange("source_notes")}
                  fullWidth
                  multiline
                  minRows={2}
                  sx={{ gridColumn: { xs: "auto", md: "1 / span 2" } }}
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Resource reserves in forecast
              </Typography>
              <Autocomplete
                multiple
                options={assetsAvailable}
                getOptionLabel={(option) => option.asset_name || "Resource reserve"}
                value={assetsAvailable.filter((asset) => selectedAssetIds.includes(asset.id))}
                onChange={handleAssetsChange}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select resource reserves" />
                )}
              />
            </Box>

            <Divider />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "260px 1fr" },
                gap: 2,
                alignItems: "start",
              }}
            >
              <Box
                sx={{
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: 2,
                  p: 1.5,
                  backgroundColor: "rgba(0,0,0,0.02)",
                  maxHeight: { md: "60vh" },
                  overflowY: "auto",
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  Resource reserves in forecast
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {selectedAssetIds.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Select resource reserves to begin.
                    </Typography>
                  )}
                  {selectedAssetIds.map((assetId) => {
                    const asset = assetsById.get(assetId);
                    const status = getAssetStatus(assetId);
                    const isActive = focusedAssetId === assetId;
                    return (
                      <Box
                        key={assetId}
                        onClick={() => handleFocusAsset(assetId)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") handleFocusAsset(assetId);
                        }}
                        sx={{
                          border: isActive
                            ? "1px solid rgba(0,0,0,0.4)"
                            : "1px solid transparent",
                          borderRadius: 1,
                          p: 1,
                          backgroundColor: isActive ? "#fff" : "transparent",
                          cursor: "pointer",
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {asset?.asset_name || "Resource reserve"}
                        </Typography>
                        <Chip
                          size="small"
                          label={status.label}
                          color={status.color}
                          variant={status.color === "default" ? "outlined" : "filled"}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              </Box>

              <Box
                sx={{
                  overflowX: { xs: "scroll", md: "hidden" },
                  WebkitOverflowScrolling: "touch",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle2" fontWeight={700}>
                    {focusedAsset ? `Forecast grid: ${focusedAsset.asset_name}` : "Forecast grid"}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => handleNavigateAsset("prev")}
                      disabled={!focusedAssetId || selectedAssetIds.indexOf(focusedAssetId) === 0}
                    >
                      Previous reserve
                    </Button>
                    <Button
                      size="small"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => handleNavigateAsset("next")}
                      disabled={
                        !focusedAssetId ||
                        selectedAssetIds.indexOf(focusedAssetId) === selectedAssetIds.length - 1
                      }
                    >
                      Next reserve
                    </Button>
                  </Stack>
                </Stack>
                {!focusedAsset ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Select a resource reserve from the outline to start editing forecast values.
                  </Typography>
                ) : (
                  <Box sx={{ mt: 2 }}>
                    <Box
                      sx={{
                        border: "1px solid rgba(0,0,0,0.08)",
                        borderRadius: 2
                      }}
                    >
                      <Box
                        sx={{
                          overflowX: { xs: "scroll", md: "auto" },
                          WebkitOverflowScrolling: "touch",
                        }}
                      >
                        <Box sx={{ minWidth: 860 + forecastYearColumns.length * 120 }}>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: `1.4fr 1fr repeat(${forecastYearColumns.length}, 1fr)`,
                              gap: 1,
                              px: 2,
                              py: 1,
                              backgroundColor: "rgba(0,0,0,0.03)",
                            }}
                          >
                            <Typography variant="caption" fontWeight={700}>
                              Commodity
                            </Typography>
                            <Typography variant="caption" fontWeight={700}>
                              Metric
                            </Typography>
                            {forecastYearColumns.map((col) => (
                              <Typography
                                key={`forecast-${col.key}`}
                                variant="caption"
                                fontWeight={700}
                              >
                                {col.label}
                              </Typography>
                            ))}
                          </Box>
                          <Stack spacing={0} sx={{ px: 2, py: 1.5 }}>
                            {focusedRows.map((row, rowIndex) => (
                              <Box key={`forecast-row-${rowIndex}`} sx={{ mb: 1.5 }}>
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: `1.4fr 1fr repeat(${forecastYearColumns.length}, 1fr)`,
                                    gap: 1,
                                    alignItems: "center",
                                    py: 0.5,
                                  }}
                                >
                                  <TextField
                                    select
                                    size="small"
                                    value={row.commodity_id}
                                    onChange={(event) =>
                                      handleCommodityChange(rowIndex, event.target.value)
                                    }
                                    fullWidth
                                    required
                                  >
                                    <MenuItem value="">Select commodity</MenuItem>
                                    {commodities.map((commodity) => (
                                      <MenuItem key={commodity.id} value={commodity.id}>
                                        {commodity.name}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                  <Typography variant="caption" color="text.secondary">
                                    Ounces produced ({metricUnits?.ounces_produced || "oz"})
                                  </Typography>
                                  {forecastYearColumns.map((col) => (
                                    <TextField
                                      key={`forecast-${rowIndex}-ounces-${col.key}`}
                                      size="small"
                                      value={row.values.ounces_produced?.[col.year] || ""}
                                      onChange={(event) =>
                                        handleValueChange(
                                          rowIndex,
                                          "ounces_produced",
                                          col.year,
                                          event.target.value
                                        )
                                      }
                                      type="number"
                                      fullWidth
                                    />
                                  ))}
                                </Box>
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: `1.4fr 1fr repeat(${forecastYearColumns.length}, 1fr)`,
                                    gap: 1,
                                    alignItems: "center",
                                    py: 0.5,
                                  }}
                                >
                                  <Box />
                                  <Typography variant="caption" color="text.secondary">
                                    AISC ({formatProductionUnit(
                                      metricUnits?.aisc || "/oz",
                                      draftForecastMeta.reporting_currency
                                    )})
                                  </Typography>
                                  {forecastYearColumns.map((col) => (
                                    <TextField
                                      key={`forecast-${rowIndex}-aisc-${col.key}`}
                                      size="small"
                                      value={row.values.aisc?.[col.year] || ""}
                                      onChange={(event) =>
                                        handleValueChange(
                                          rowIndex,
                                          "aisc",
                                          col.year,
                                          event.target.value
                                        )
                                      }
                                      type="number"
                                      fullWidth
                                    />
                                  ))}
                                </Box>
                                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
                                  <Button
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveCommodityRow(rowIndex)}
                                    disabled={focusedRows.length === 1}
                                  >
                                    Remove commodity
                                  </Button>
                                </Box>
                              </Box>
                            ))}
                            {focusedRows.length === 0 && (
                              <Typography variant="body2" color="text.secondary">
                                No forecast rows yet.
                              </Typography>
                            )}
                            <Button
                              size="small"
                              onClick={handleAddCommodityRow}
                              sx={{ alignSelf: "flex-start" }}
                            >
                              + Add commodity row
                            </Button>
                          </Stack>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveForecast}
            disabled={saveState.loading || !canEdit}
          >
            {saveState.loading ? "Saving..." : "Save Forecast"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteForecastTarget)}
        onClose={() => setDeleteForecastTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete forecast?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will delete all resource forecast reports in{" "}
            <strong>{deleteForecastTarget?.name || "this forecast"}</strong>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteForecastTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDeleteForecast}
          >
            Confirm delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
